/**
 * R-9 — versioned prompt template for cross-section coherence judge (Gemini Flash JSON).
 */
export const SECTION_COHERENCE_JUDGE_PROMPT_VERSION = 'section-coherence-judge-v1';

export interface SectionCoherenceJudgeInput {
  reportType: string;
  topic: string;
  sectionAId: string;
  sectionAContent: string;
  sectionBId: string;
  sectionBContent: string;
}

export interface SectionCoherenceHarmonizeInput {
  reportType: string;
  topic: string;
  sectionAId: string;
  sectionAContent: string;
  sectionBId: string;
  sectionBContent: string;
  rationale: string;
}

const MAX_SECTION_CHARS = 4000;

function clip(text: string): string {
  const t = text.trim();
  return t.length <= MAX_SECTION_CHARS ? t : `${t.slice(0, MAX_SECTION_CHARS)}…`;
}

/** Build the Flash judge prompt for one section pair. */
export function buildSectionCoherenceJudgePrompt(input: SectionCoherenceJudgeInput): string {
  return [
    `PROMPT_VERSION: ${SECTION_COHERENCE_JUDGE_PROMPT_VERSION}`,
    'You are a report QA auditor. Compare TWO sections from the SAME report.',
    'Determine whether they make contradicting claims about the same underlying risk, opportunity, or quantitative fact.',
    'Return JSON only — no markdown fences.',
    '',
    `Report type: ${input.reportType}`,
    `Audit topic: ${input.topic}`,
    '',
    'Severity rubric for contradictionSeverity:',
    '- critical: direct opposite verdicts on the same fact (e.g. "favorable interconnection" vs "high interconnection risk")',
    '- major: materially different risk posture that would confuse an investor',
    '- minor: nuance mismatch or emphasis difference without opposite verdicts',
    '- none: sections are aligned or address different facets without conflict',
    '',
    `SECTION A (${input.sectionAId}):`,
    clip(input.sectionAContent),
    '',
    `SECTION B (${input.sectionBId}):`,
    clip(input.sectionBContent),
  ].join('\n');
}

/** Optional autocorrect — suggest harmonized wording (does not rewrite automatically). */
export function buildSectionCoherenceHarmonizePrompt(input: SectionCoherenceHarmonizeInput): string {
  return [
    `PROMPT_VERSION: ${SECTION_COHERENCE_JUDGE_PROMPT_VERSION}-harmonize`,
    'Two report sections contain a coherence tension. Suggest ONE sentence that could replace',
    'the weaker/misaligned phrasing to align both sections. Do not invent new facts.',
    'Return JSON only.',
    '',
    `Report type: ${input.reportType}`,
    `Topic: ${input.topic}`,
    `Tension: ${input.rationale}`,
    '',
    `SECTION A (${input.sectionAId}):`,
    clip(input.sectionAContent),
    '',
    `SECTION B (${input.sectionBId}):`,
    clip(input.sectionBContent),
  ].join('\n');
}
